import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Editor, EditorManager } from 'tinymce';
import { HasClassName } from '../component-utils';
import { useForceUpdate, useSelector } from '../hook-utils';
import { setInstantMessageUrgent } from '../store/actions';
import { getInstantMessageUrgent } from '../store/reducers/instantmessager';
import './HtmlEditor.scss';
import { ReactComponent as SendIcon } from './icons/instantmessager/send.svg';
import { ReactComponent as AlignCenterIcon } from './icons/toolbar/align_center.svg';
import { ReactComponent as AlignJustifyIcon } from './icons/toolbar/align_justify.svg';
import { ReactComponent as AlignLeftIcon } from './icons/toolbar/align_left.svg';
import { ReactComponent as AlignRightIcon } from './icons/toolbar/align_right.svg';
import { ReactComponent as BoldIcon } from './icons/toolbar/bold.svg';
import { ReactComponent as CopyIcon } from './icons/toolbar/copy.svg';
import { ReactComponent as CutIcon } from './icons/toolbar/cut.svg';
//import { ReactComponent as BulletListIcon } from './icons/toolbar/bullet_list.svg';
//import { ReactComponent as DecIndentIcon } from './icons/toolbar/dec_indent.svg';
//import { ReactComponent as IncIndentIcon } from './icons/toolbar/inc_indent.svg';
import { ReactComponent as ItalicIcon } from './icons/toolbar/italic.svg';
import { ReactComponent as PasteIcon } from './icons/toolbar/paste.svg';
//import { ReactComponent as NumberListIcon } from './icons/toolbar/number_list.svg';
import { ReactComponent as RedoIcon } from './icons/toolbar/redo.svg';
import { ReactComponent as UnderlineIcon } from './icons/toolbar/underline.svg';
import { ReactComponent as UndoIcon } from './icons/toolbar/undo.svg';
import { ReactComponent as UrgentIcon } from './icons/toolbar/urgent.svg';


interface EditorAction {
  is(editor: Editor): boolean
  act(editor: Editor): void
}

interface Props extends HasClassName {
  value: string
  onChange(value: string): void
  disabled?: boolean
  autoFocus?: boolean
  editorRef?: React.MutableRefObject<HtmlEditorApi | null>
  onEnterKeyPress?(): void
  onSendButtonClick?(): void
  urgent?:boolean
}

const FormatOptions = { format: 'html' }
declare const tinymce: EditorManager

export default function HtmlEditor({ className, value, onChange, disabled, autoFocus, editorRef: editorRefExt, onEnterKeyPress, onSendButtonClick, urgent }: Props) {
  const onChangeRef = useRef(onChange)
  const valueRef = useRef<string>('')
  const shouldUpdate = useRef(true)
  const forceUpdate = useForceUpdate()
  const onKeyPressRef = useRef(onEnterKeyPress)
  const dispatch = useDispatch()
  const urgentState = useSelector(getInstantMessageUrgent)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    onKeyPressRef.current = onEnterKeyPress
  }, [onEnterKeyPress])

  const autoFocusRef = useRef(autoFocus)
  useEffect(() => {
    autoFocusRef.current = autoFocus
  }, [autoFocus])

  const apiRef = useRef<HtmlEditorApi | null>(null)
  const [editor, setEditor] = useState<Editor>()

  const onLoaded = useCallback((ref: HTMLDivElement | null) => {
    if (!ref) {
      setEditor(undefined)
      return
    }

    tinymce.init({
      target: ref,
      inline: true,
      toolbar: false,
      menubar : false,
      setup(ed) {
        ed.on('init', () => {
          setEditor(ed)
        })
      },
    })
  }, [])

  useEffect(() => {
    if (!editor) { return }

    const onUpdateEvents = 'Change keyup SetContent NodeChange'

    const onUpdate = () => {
      if (!shouldUpdate.current) { return }

      const value = editor.getContent(FormatOptions)
      if (valueRef.current !== value) {
        valueRef.current = value
        onChangeRef.current(valueRef.current)

        // update toolbar
        forceUpdate()
      }
    }

    function onSelectionChange() {
      // update toolbar
      forceUpdate()
    }

    function onKeyPress(e: KeyboardEvent) {
      if ((!e.ctrlKey && e.keyCode === 10) ||
        (!e.ctrlKey && e.keyCode === 13)) {
        if (onKeyPressRef.current) {
          onKeyPressRef.current()

          e.cancelBubble = true
          return false
        }
      }
    }

    editor.on(onUpdateEvents, onUpdate)
    editor.on('SelectionChange', onSelectionChange)
    editor.on('keydown', onKeyPress)

    if (autoFocusRef.current) {
      editor.focus(false)
    }

    apiRef.current = {
      pasteContent(html) {
        editor.selection.setContent(html, FormatOptions)
      },
      focus() {
        editor.focus(false)
      },
    }

    return () => {
      editor.off(onUpdateEvents, onUpdate)
      editor.off('SelectionChange', onSelectionChange)
      apiRef.current = null
    }
  }, [editor, forceUpdate, onEnterKeyPress])

  useEffect(() => {
    if (value === valueRef.current) { return }
    if (!editor) { return }

    shouldUpdate.current = false
    editor.setContent(value, FormatOptions)
    editor.undoManager.clear()
    valueRef.current = editor.getContent(FormatOptions)
    shouldUpdate.current = true
  }, [editor, value])

  useEffect(() => {
    if (!editor) { return }
    editor.setMode(disabled ? 'readonly' : 'design')
  }, [disabled, editor])

  useEffect(() => {
    if (editorRefExt) {
      editorRefExt.current = apiRef.current
    }
  })

  function button(action: EditorAction, element: React.ReactNode) {
    function onClick() {
      if (!editor) { return }
      editor.focus(false)
      action.act(editor)
    }

    function focused() {
      return editor && editor.hasFocus()
    }

    return <button className={classNames(editor && focused() && action.is(editor) && 'active')} onClick={onClick}>
      {element}
    </button>
  }

  return <div className={classNames('HtmlEditor', className)}>
    <div className='toolbar'>
      {button(undoRedo(false), <UndoIcon />)}
      {button(undoRedo(true), <RedoIcon />)}

      <div className='separator' />

      {button(sendClipboardCommand('cut'), <CutIcon />)}
      {button(sendClipboardCommand('copy'), <CopyIcon />)}
      {button(sendClipboardCommand('paste'), <PasteIcon />)}

      <div className='separator' />

      {button(toggleFormat('bold'), <BoldIcon />)}
      {button(toggleFormat('italic'), <ItalicIcon />)}
      {button(toggleFormat('underline'), <UnderlineIcon />)}

      <div className='separator' />

      {button(format('alignleft', ['aligncenter', 'alignright', 'alignjustify']), <AlignLeftIcon />)}
      {button(format('aligncenter'), <AlignCenterIcon />)}
      {button(format('alignright'), <AlignRightIcon />)}
      {button(format('alignjustify'), <AlignJustifyIcon />)}

      {urgent && <div className='separator' />}
      {urgent && <button title='Mark next message as high priority' className={classNames('urgent', urgentState && 'active')} onClick={()=> dispatch(setInstantMessageUrgent())}><UrgentIcon/></button>}

      {/*
      <div className='separator' />

      {button(format('indent', '-1'), <DecIndentIcon />)}
      {button(format('indent', '+1'), <IncIndentIcon />)}
      {button(toggleFormat('list', 'bullet'), <BulletListIcon />)}
      {button(toggleFormat('list', 'ordered'), <NumberListIcon />)}
      */}

      {onSendButtonClick && <button className={classNames('send')} onClick={()=> onSendButtonClick()}><SendIcon/></button>}
    </div>

    <div className='body' ref={onLoaded} />
  </div>
}

function format(name: string, not?: string[]): EditorAction {
  return {
    is: editor => editor.formatter.match(name) || not !== undefined && editor.formatter.matchAll(not).length === 0,
    act: editor => editor.formatter.apply(name),
  }
}

function toggleFormat(name: string, value: any = true): EditorAction {
  return {
    is: editor => editor.formatter.match(name),
    act: editor => {
      if (editor.formatter.match(name)) {
        editor.formatter.remove(name)
      } else {
        editor.formatter.apply(name)
      }
    },
  }
}

function sendClipboardCommand(command: 'cut' | 'copy' | 'paste'): EditorAction {
  return {
    is: () => false,
    async act(editor) {

      if (command === 'cut' || command === 'copy') {
        const sel = editor.selection.getContent(FormatOptions)
        if (!sel) { return }

        document.execCommand('copy')

        if (command === 'cut') {
          editor.selection.setContent('', FormatOptions)
        }
      } else {
        if (!document.execCommand('paste')) {
          alert('Your browser doesn\'t support pasting. Please use ctrl+V instead')
        }
      }
    },
  }
}

function undoRedo(redo: boolean): EditorAction {
  return {
    is: () => false,
    act: editor => {
      if (!redo) {
        editor.undoManager.undo()
      } else {
        editor.undoManager.redo()
      }
    },
  }
}

export interface HtmlEditorApi {
  pasteContent(html: string): void
  focus(): void
}
